import React from 'react';
import PropTypes from 'prop-types';
import { values, round, get, pick, pickBy, capitalize } from 'lodash';
import { connect } from 'react-redux';
import {
  ButtonGroup, Button, Modal, Table,
} from 'react-bootstrap';
import { bindActionCreators } from 'redux';
import s from '../../../../redux/selectors';
import a from '../../../../redux/actions';
import types from '../../../../types';
import { wrap, withT } from '../../../../lib/wrappers';
import { displayLocation } from '../../../../lib/locations';
import { userPledgeStatus } from '../../../../lib/userApi';
import actionHelpers from '../../../../lib/actions';
import { CustomizableLabelBootstrap } from '../../../common/Customizable';

const displayUserName = user =>
  (user.lastName ?
    `${user.firstName} ${user.lastName}` :
    user.firstName);

const areSocialLinksEmpty = user => user.socialLinks &&
  !values(user.socialLinks).some(value => Boolean(value));

const SocialLinks = ({ user }) => (
  <div style={{ display: 'flex', justifyContent: 'center' }}>
    <ButtonGroup>
      {get(user, 'socialLinks.website') &&
        <a className="btn btn-default" href={get(user, 'socialLinks.website')} target="_blank" rel="noreferrer noopener"><i className="fa fa-link" /></a>
      }
      {get(user, 'socialLinks.medium') &&
        <a className="btn btn-default" href={`https://medium.com/@${get(user, 'socialLinks.medium')}`} target="_blank" rel="noreferrer noopener"><i className="fa fa-medium" /></a>
      }
      {get(user, 'socialLinks.facebook') &&
        <a className="btn btn-default" href={get(user, 'socialLinks.facebook')} target="_blank" rel="noreferrer noopener"><i className="fa fa-facebook-f" /></a>
      }
      {get(user, 'socialLinks.twitter') &&
        <a className="btn btn-default" href={`https://twitter.com/${get(user, 'socialLinks.twitter')}`} target="_blank" rel="noreferrer noopener"><i className="fa fa-twitter" /></a>
      }
      {get(user, 'socialLinks.linkedin') &&
        <a className="btn btn-default" href={get(user, 'socialLinks.linkedin')} target="_blank" rel="noreferrer noopener"><i className="fa fa-linkedin" /></a>
      }
      {get(user, 'socialLinks.instagram') &&
        <a className="btn btn-default" href={`https://www.instagram.com/${get(user, 'socialLinks.instagram')}/`} target="_blank" rel="noreferrer noopener"><i className="fa fa-instagram" /></a>
      }
    </ButtonGroup>
  </div>
);

SocialLinks.propTypes = {
  user: types.User.isRequired,
};

const UserModal = ({
  t, modalLeader, toggleModalLeader,
  modalLeaderPledges, widgetExists, widgetWindow, ui,
}) => {
  if (!modalLeader) return (<div>&nbsp;</div>);
  const pledges = modalLeaderPledges[modalLeader.id];
  const completedActions = pickBy(pledges,
    (pledge, _key) => pledge.status === userPledgeStatus.COMPLETED);
  const pledgedActions = pickBy(pledges,
    (pledge, _key) => pledge.status === userPledgeStatus.PLEDGED);
  const style = { visibility: modalLeader ? 'visible' : 'hidden' };
  if (widgetExists) style.top = widgetWindow.scrollY;
  return (
    <Modal
      id="leaders__user_modal"
      style={style}
      show={Boolean(modalLeader)}
      onHide={() => toggleModalLeader()}
      backdrop
    >
      <Modal.Header>
        <Modal.Title>{displayUserName(modalLeader)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'stretch' }}
        >
          <div
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center' }}
          >
            {!modalLeader.profilePhoto &&
              <img
                style={{ height: '80px', padding: '10px' }}
                alt={t('Profile photo')}
                src={'/assets/img/account-active.png'}
              />
            }
            {modalLeader.profilePhoto &&
              <img
                style={{ maxHeight: '150px', padding: '10px' }}
                alt={t('Profile photo')}
                src={modalLeader.profilePhoto}
              />
            }
            <div style={{ alignContent: 'center', fontStyle: 'italic', fontSize: '11px' }}>
              <i className="fa fa-map-marker" />&nbsp;
              {displayLocation(modalLeader)}
            </div>
          </div>
          <div
            style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', padding: '10px', width: '100%' }}
          >
            <div style={{ width: '100%' }}>{modalLeader.descriptionMD}</div>
            {!areSocialLinksEmpty(modalLeader) && <SocialLinks user={modalLeader} />}
          </div>
        </div>
        <Table style={{ marginTop: '10px', marginBottom: '0px' }}>
          <thead>
            <tr>
              <th>{capitalize(t('Travel'))}</th>
              <th>{capitalize(t('Home'))}</th>
              <th>{capitalize(t('Food'))}</th>
              <th>{capitalize(t('Shopping'))}</th>
              <th>{capitalize(t('Total'))}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{round(modalLeader.result_transport_total, 2)}</td>
              <td>{round(modalLeader.result_housing_total, 2)}</td>
              <td>{round(modalLeader.result_food_total, 2)}</td>
              <td>
                {round(modalLeader.result_goods_total + modalLeader.result_services_total, 2)}
              </td>
              <td>{round(modalLeader.result_grand_total, 2)}</td>
            </tr>
          </tbody>
        </Table>
        <div style={{ textAlign: 'center' }}><small><i
          dangerouslySetInnerHTML={{
            __html: t('annual tons CO<sub>2</sub>eq'),
          }}
        /></small></div>
        <div>
          <h5>{t('Completed Actions')}</h5>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {Object.keys(completedActions).map(actionKey => (
              <CustomizableLabelBootstrap
                ui={ui}
                key={actionKey}
                style={{ backgroundColor: '#449D44', margin: '3px' }}
              >{actionHelpers.getLabel(actionKey, t)}</CustomizableLabelBootstrap>
            ))}
          </div>
        </div>
        <div>
          <h5>{t('Pledged Actions')}</h5>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {Object.keys(pledgedActions).map(actionKey => (
              <CustomizableLabelBootstrap
                ui={ui}
                key={actionKey}
                style={{ backgroundColor: '#74c27c', margin: '3px' }}
              >{actionHelpers.getLabel(actionKey, t)}</CustomizableLabelBootstrap>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          id="leaders__user_modal__close"
          onClick={() => toggleModalLeader()}
        >{t('Close')}</Button>
      </Modal.Footer>
    </Modal>
  );
};

UserModal.propTypes = {
  t: PropTypes.func.isRequired,
  toggleModalLeader: PropTypes.func.isRequired,
  modalLeader: types.CalculatorResult,
  modalLeaderPledges: types.UserPledges.isRequired,
  widgetExists: PropTypes.bool.isRequired,
  widgetWindow: types.WidgetWindow,
  ui: PropTypes.any,
};

const mapStateToProps = (state, ownProps) => [
  'modalLeader',
  'modalLeaderPledges',
  'widgetExists',
  'widgetWindow',
  'ui',
].reduce((props, selector) => ({
  ...props,
  [selector]: s[selector](state, ownProps),
}), {});

const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'toggleModalLeader',
  ]),
  dispatch,
);

export default wrap(
  withT('leaders'),
  connect(mapStateToProps, mapDispatchToProps)(UserModal),
);

